import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Eye } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerImages = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (error) {
      console.error('Failed to fetch images:', error);
      toast.error(t('customerImages.errorLoadingImages'));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('customerImages.pleaseUploadImage'));
      return;
    }

    // Validate file size (max 20MB)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t('customerImages.imageTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/images/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      toast.success(t('customerImages.imageUploaded'));
      fetchImages();
    } catch (error) {
      console.error('Upload error:', error.response?.data || error.message);
      toast.error(t('customerImages.errorUploadingImage', { error: error.response?.data?.detail || error.message }));
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!await confirm.danger(t('customerImages.confirmDeleteImage'))) return;

    try {
      const token = getToken();
      await axios.delete(`${API}/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerImages.imageDeleted'));
      setImages(images.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('customerImages.errorDeletingImage'));
    }
  };

  const handleReplace = async (imageId, file) => {
    if (!file) return;

    setUploading(true);
    try {
      const token = getToken();
      
      // First delete the old image
      await axios.delete(`${API}/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then upload the new one
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/images/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('customerImages.imageReplaced'));
      fetchImages();
    } catch (error) {
      console.error('Replace error:', error);
      toast.error(t('customerImages.errorReplacingImage'));
    } finally {
      setUploading(false);
    }
  };

  // Helper function to get optimized image URL for display
  const getImageUrl = (image, options = {}) => {
    const url = image.image_url;
    
    // Handle AWS CloudFront URLs - return as-is (optimized at upload)
    if (url.includes('cloudfront.net') || url.includes('amazonaws.com')) {
      return url;
    }
    
    // If URL starts with /uploads, /files, or /api/files, it's relative to BACKEND_URL
    if (url.startsWith('/uploads') || url.startsWith('/files') || url.startsWith('/api/files')) {
      return `${BACKEND_URL}${url}`;
    }
    
    // Otherwise return as-is
    return url;
  };

  return (
    <CustomerLayout>
      <div className="admin-header">
        <h1>{t('customerImages.title')}</h1>
        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
          <Plus size={20} /> {t('customerImages.uploadImage')}
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </label>
      </div>

      {uploading && (
        <div style={{ 
          padding: '1rem', 
          background: 'rgba(80, 227, 194, 0.1)', 
          borderRadius: '8px',
          marginTop: '1rem',
          textAlign: 'center'
        }}>
          {t('customerImages.uploading')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
        {images.map(image => {
          // Use optimized thumbnail for gallery
          const thumbnailUrl = getImageUrl(image, { width: 400, height: 300, quality: 'auto:good' });
          // Full resolution for preview
          const fullUrl = getImageUrl(image);
          console.log('Rendering image:', image.id, 'Thumbnail:', thumbnailUrl);
          return (
            <div key={image.id} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
              <img
                src={thumbnailUrl}
                alt="Uploaded"
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                onLoad={() => {
                  console.log('Image loaded successfully:', thumbnailUrl);
                }}
                onError={(e) => {
                  console.error('Image load error:', thumbnailUrl, e);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{ 
                display: 'none',
                width: '100%', 
                height: '200px', 
                background: 'rgba(255,0,0,0.1)',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#ff6b6b',
                padding: '1rem',
                fontSize: '0.9rem'
              }}>
                <div>{t('customerImages.couldNotLoadImage')}</div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', wordBreak: 'break-all' }}>
                  {thumbnailUrl}
                </div>
              </div>
              <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => window.open(fullUrl, '_blank')}
                  title={t('customerImages.previewImage')}
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                    color: '#2A2A2A',
                    border: '1px solid rgba(255, 255, 255, 0.95)'
                  }}
                >
                  <Eye size={16} />
                </button>
                <label 
                  className="btn btn-secondary" 
                  style={{ 
                    cursor: 'pointer', 
                    margin: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                    color: '#2A2A2A',
                    border: '1px solid rgba(255, 255, 255, 0.95)'
                  }} 
                  title={t('customerImages.replaceImage')}
                >
                  <RefreshCw size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleReplace(image.id, e.target.files[0])}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                </label>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(image.id)}
                  title={t('customerImages.deleteImage')}
                  style={{ 
                    backgroundColor: 'rgba(220, 53, 69, 0.95)',
                    border: '1px solid rgba(220, 53, 69, 0.95)'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {images.length === 0 && !uploading && (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#808080' }}>
          <p>{t('customerImages.noImagesYet')}</p>
          <p>{t('customerImages.uploadImagesDescription')}</p>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerImages;
